<template>
  <div 
    class="drawing-board-container" 
    :style="containerStyle"
  >
    <!-- 显示按钮（仅在隐藏时显示） -->
    <div v-if="!isVisible" class="show-button-container">
      <button @click="toggleVisibility" class="icon-button show-button">
        <img src="@/assets/draw/show.png" alt="显示" />
      </button>
    </div>

    <!-- 主要内容（显示时显示） -->
    <div v-if="isVisible" class="main-content">
      <!-- 画板 -->
      <div class="canvas-container">
        <vue-drawing-canvas
          ref="drawingCanvas"
          :canvasId="canvasId"
          :width="canvasWidth"
          :height="canvasHeight"
          :strokeType="strokeType"
          :fillShape="fillShape"
          :eraser="eraser"
          :color="color"
          :lineWidth="lineWidth"
          :lineCap="lineCap"
          :lineJoin="lineJoin"
          :backgroundColor="backgroundColor"
          :backgroundImage="backgroundImage"
          :saveAs="saveAs"
          v-model:image="canvasImage"
          @mousemove="handleMouseMove"
        />
      </div>

      <!-- 右侧工具按钮 -->
      <div class="right-tools">
        <button @click="toggleEraser" class="icon-button eraser-button">
          <img :src="eraser ? require('@/assets/draw/xp-y.png') : require('@/assets/draw/xp-n.png')" alt="橡皮擦" />
        </button>
        <button @click="undoDrawing" class="icon-button undo-button">
          <img src="@/assets/draw/back.png" alt="撤销" />
        </button>
        <button @click="clearCanvas" class="icon-button clear-button">
          <img src="@/assets/draw/clear.png" alt="清空" />
        </button>
        <button @click="confirmDrawing" class="icon-button confirm-button">
          <img src="@/assets/draw/confirm.png" alt="确定" />
        </button>
        <button @click="toggleVisibility" class="icon-button hide-button">
          <img src="@/assets/draw/hide.png" alt="隐藏" />
        </button>
      </div>

      <!-- 底部颜色和线宽控制 -->
      <div class="bottom-controls">
        <div class="control-group">
          <label>颜色:</label>
          <input type="color" v-model="color" @change="updateColor" />
        </div>
        
        <div class="control-group">
          <label>线宽:</label>
          <input 
            type="range" 
            min="1" 
            max="20" 
            v-model="lineWidth" 
            @input="updateLineWidth"
          />
          <span>{{ lineWidth }}px</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VueDrawingCanvasWrapper from './VueDrawingCanvasWrapper.vue'

export default {
  name: 'DrawingBoard',
  components: {
    VueDrawingCanvas: VueDrawingCanvasWrapper
  },
  props: {
    // 画板尺寸
    width: {
      type: Number,
      default: 800
    },
    height: {
      type: Number,
      default: 600
    },
    // 画板位置
    left: {
      type: Number,
      default: 0
    },
    top: {
      type: Number,
      default: 0
    },
    // z-index
    zIndex: {
      type: Number,
      default: 1000
    },
    // 初始背景色
    initialBackgroundColor: {
      type: String,
      default: '#FFFFFF'
    },
    // 初始背景图片
    initialBackgroundImage: {
      type: String,
      default: ''
    },
    // 是否显示画板
    visible: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      canvasId: 'drawing-canvas-' + Date.now(),
      canvasWidth: this.width,
      canvasHeight: this.height,
      strokeType: 'dash', // 默认使用自由画笔实线
      fillShape: false,
      eraser: false,
      color: '#000000',
      lineWidth: 5,
      lineCap: 'round',
      lineJoin: 'round',
      backgroundColor: this.initialBackgroundColor,
      backgroundImage: this.initialBackgroundImage,
      saveAs: 'png',
      canvasImage: '',
      isVisible: this.visible
    }
  },
  computed: {
    containerStyle() {
      return {
        position: 'fixed',
        left: this.left + 'px',
        top: this.top + 'px',
        width: this.width + 'px',
        height: this.height + 'px',
        zIndex: this.zIndex
      }
    }
  },
  methods: {
    updateColor() {
      this.$emit('color-changed', this.color)
    },
    updateLineWidth() {
      this.$emit('line-width-changed', this.lineWidth)
    },
    updateEraser() {
      this.$emit('eraser-changed', this.eraser)
    },
    toggleEraser() {
      this.eraser = !this.eraser
      this.updateEraser()
    },
    undoDrawing() {
      if (this.$refs.drawingCanvas) {
        // 调用vue-drawing-canvas的undo方法
        this.$refs.drawingCanvas.undo()
        this.$emit('drawing-undone')
      }
    },
    clearCanvas() {
      if (this.$refs.drawingCanvas) {
        // 清空画板内容但保留背景
        this.$refs.drawingCanvas.reset()
        this.$emit('canvas-cleared')
      }
    },
    confirmDrawing() {
      // 发送确定事件，包含画板图片数据
      try {
        // 尝试获取canvas图片数据
        if (this.$refs.drawingCanvas && this.$refs.drawingCanvas.$refs.canvas) {
          const canvas = this.$refs.drawingCanvas.$refs.canvas;
          const imageData = canvas.toDataURL('image/png');
          this.$emit('confirm-drawing', imageData);
        } else {
          // 如果无法获取canvas，使用当前的canvasImage
          this.$emit('confirm-drawing', this.canvasImage);
        }
      } catch (error) {
        console.error('获取画板图片失败:', error);
        // 如果toDataURL失败，尝试使用组件内部的图片数据
        if (this.canvasImage) {
          this.$emit('confirm-drawing', this.canvasImage);
        } else {
          // 创建一个空白的canvas作为fallback
          const fallbackCanvas = document.createElement('canvas');
          fallbackCanvas.width = this.canvasWidth;
          fallbackCanvas.height = this.canvasHeight;
          const ctx = fallbackCanvas.getContext('2d');
          ctx.fillStyle = this.backgroundColor || '#ffffff';
          ctx.fillRect(0, 0, this.canvasWidth, this.canvasHeight);
          const fallbackImage = fallbackCanvas.toDataURL('image/png');
          this.$emit('confirm-drawing', fallbackImage);
        }
      }
    },
    toggleVisibility() {
      this.isVisible = !this.isVisible
      this.$emit('visibility-changed', this.isVisible)
    },
    handleMouseMove(event) {
      this.$emit('mouse-move', event)
    },
    // 公开方法供父组件调用
    getCanvasImage() {
      try {
        // 尝试从vue-drawing-canvas组件获取图片
        if (this.$refs.drawingCanvas && this.$refs.drawingCanvas.$refs.canvas) {
          const canvas = this.$refs.drawingCanvas.$refs.canvas;
          return canvas.toDataURL('image/png');
        } else {
          return this.canvasImage;
        }
      } catch (error) {
        console.error('获取Canvas图片失败:', error);
        return this.canvasImage || '';
      }
    },
    setBackgroundImage(imageUrl) {
      // 为了避免CORS问题，特别是在本地开发环境中
      // 我们需要确保图片能够被canvas正确处理
      if (imageUrl) {
        // 创建一个新的Image对象来预加载并设置crossOrigin
        const img = new Image();
        img.crossOrigin = "anonymous";
        
        img.onload = () => {
          // 图片加载成功，设置为背景图
          this.backgroundImage = imageUrl;
        };
        
        img.onerror = () => {
          // 如果crossOrigin失败，尝试不设置crossOrigin
          console.warn('图片加载失败，尝试不使用crossOrigin');
          const imgFallback = new Image();
          imgFallback.onload = () => {
            this.backgroundImage = imageUrl;
          };
          imgFallback.onerror = () => {
            console.error('图片加载失败:', imageUrl);
          };
          imgFallback.src = imageUrl;
        };
        
        img.src = imageUrl;
      } else {
        this.backgroundImage = imageUrl;
      }
    },
    setBackgroundColor(color) {
      this.backgroundColor = color
    },
    show() {
      this.isVisible = true
    },
    hide() {
      this.isVisible = false
    }
  },
  watch: {
    canvasImage(newImage) {
      this.$emit('image-updated', newImage)
    },
    visible(newVal) {
      this.isVisible = newVal
    },
    initialBackgroundImage(newVal) {
      this.backgroundImage = newVal
    }
  }
}
</script>

<style scoped>
.drawing-board-container {
  position: relative;
}

/* 显示按钮容器（隐藏状态） */
.show-button-container {
  display: flex;
  justify-content: center;
  align-items: center;
}

/* 主要内容容器 */
.main-content {
  position: relative;
  display: flex;
  flex-direction: column;
  border: 1px solid #ddd;
  border-radius: 8px;
  background-color: #f9f9f9;
}

/* 画板容器 */
.canvas-container {
  display: flex;
  justify-content: center;
  padding: 8px;
  background-color: #fff;
  border-radius: 6px;
}

/* 右侧工具按钮 */
.right-tools {
  position: absolute;
  right: -40px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  gap: 8px;
  z-index: 1001;
  padding: 8px;
  background-color: #fff;
  border: 1px solid #ddd;
  border-radius: 6px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

/* 图标按钮样式 */
.icon-button {
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 3px;
  transition: background-color 0.2s;
}

.icon-button:hover {
  background-color: rgba(0, 0, 0, 0.1);
}

.icon-button img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

/* 底部控制面板 */
.bottom-controls {
  position: absolute;
  bottom: -40px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 20px;
  padding: 8px 16px;
  background-color: #fff;
  border: 1px solid #ddd;
  border-radius: 6px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  z-index: 1001;
}

.control-group {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
}

.control-group label {
  font-weight: 500;
  color: #333;
  white-space: nowrap;
}

.control-group input[type="color"] {
  width: 30px;
  height: 20px;
  padding: 0;
  border: 1px solid #ccc;
  border-radius: 4px;
  cursor: pointer;
}

.control-group input[type="range"] {
  width: 80px;
}

.control-group span {
  font-size: 11px;
  color: #666;
  min-width: 30px;
}
</style>